import {AxiosError} from 'axios';
import {QueryKey, UseMutationOptions, useQueryClient} from '@tanstack/react-query';

type Context<T> = {
  previous?: [QueryKey, T | undefined][];
}

type Options<T, V> = Partial<UseMutationOptions<unknown, AxiosError, V, Context<T>>> & {
  optimisticUpdateQueryKey: QueryKey | QueryKey[];
  onOptimisticUpdate(input: T | undefined, variables: V): T | undefined;
}

function toQueryKeys(key: QueryKey | QueryKey[]): QueryKey[] {
  return Array.isArray(key[0]) ? (key as QueryKey[]) : [key as QueryKey];
}

export function useOptimisticUpdate<T, V>(options: Options<T, V>): Options<T, V> {
  const queryKeys = toQueryKeys(options.optimisticUpdateQueryKey);
  const mutationKey = queryKeys[0];
  const queryClient = useQueryClient();

  return {
    ...options,
    mutationKey,
    async onMutate(variables) {
      options.onMutate?.(variables);
      const previous: [QueryKey, T | undefined][] = [];
      for (const queryKey of queryKeys) {
        await queryClient.cancelQueries({ queryKey });
        previous.push(...queryClient.getQueriesData<T>({ queryKey }));
        queryClient.setQueriesData<T>({ queryKey }, (input) => options.onOptimisticUpdate(input, variables));
      }

      return { previous };
    },
    onError(error, variables, context) {
      options.onError?.(error, variables, context);
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled(data, error, variables, context) {
      options.onSettled?.(data, error, variables, context);
      if (queryClient.isMutating({ mutationKey }) <= 1) {
        for (const queryKey of queryKeys) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
    },
  };
}
